import { Button } from '@mui/material'
import React from 'react'
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik'
import { IdlePeriodSettingsFormModel } from 'src/Libs/smart-charge/models'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import EmsIdlePeriodFormList from 'src/Libs/smart-charge/components/ems-idle-period/EmsIdlePeriodFormList'
import { EmsIdlePeriodFormField } from 'src/Libs/smart-charge/types'
import { PeriodChargeType } from 'src/Libs/smart-charge/api/types'
import useModals from 'src/Hooks/useModals'
import { DEFAULT_DAYS_SETTING } from '@e3dc-react/shell/Libs/date/const'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'

interface Props {
  model: IdlePeriodSettingsFormModel
  hasWritingRights?: boolean
}
const FIELD_LIST_NAME = 'emsIdlePeriod'
const DEFAULT_NEW_PERIOD = {
  description: '',
  isActive: true,
  startTimeOfDay: '00:00',
  endTimeOfDay: '00:00',
  type: PeriodChargeType.charge,
  daysOfWeek: { ...DEFAULT_DAYS_SETTING },
} as EmsIdlePeriodFormField

/**
 * TODO: Consider to pass the following props separately from a higher component (e.g. page) to better fit to the atom-design-principle:
 * (1) disabled (2) displayTooltip (3) titleKey
 * note: currently (1) and (2) both depend on hasWritingRights, which should be separated
 */
const EmsIdlePeriodForm: React.FC<Props> = ({ model, hasWritingRights = true }) => {
  const { openConfirmationModal } = useModals()
  const { t } = useCustomTranslation()
  const { values } = useFormikContext<IdlePeriodSettingsFormModel>()

  const listRenderer = ({ push, remove }: FieldArrayRenderProps): React.ReactNode => {
    const handleAddNew = (): void => {
      openConfirmationModal({
        title: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.addNew.title'),
        buttonText: [t('common.cancel'), t('common.ok')],
        text: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.addNew.text'),
        onConfirm: (close: () => void) => {
          push(DEFAULT_NEW_PERIOD)
          close()
        },
      })
    }

    const handleRemoveItem = (index: number): void => {
      openConfirmationModal({
        title: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.remove.title'),
        buttonText: [t('common.cancel'), t('common.ok')],
        text: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.remove.text'),
        onConfirm: (close: () => void) => {
          remove(index)
          close()
        },
      })
    }

    return (
      <SettingsFormContainer
        model={model}
        leftControls={
          <Button onClick={handleAddNew} disabled={!hasWritingRights}>
            {t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.controls.addNew')}
          </Button>
        }
      >
        <SingleToggleSettingBox
          title={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.toggle.title')}
          description={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.toggle.description')}
          name="emsGetIdlePeriodsEnable"
          disabled={!hasWritingRights}
        />
        <EmsIdlePeriodFormList
          name={FIELD_LIST_NAME}
          onRemove={handleRemoveItem}
          hasWritingRights={hasWritingRights}
          hide={!values.emsGetIdlePeriodsEnable}
        />
      </SettingsFormContainer>
    )
  }

  return <FieldArray name={FIELD_LIST_NAME} render={listRenderer} />
}

export default EmsIdlePeriodForm
